import { getMembers } from "@/company/members/services/members-service";
import { GetMembersParams, GetMembersResponse } from "@/company/members/types";
import { useAuth } from "@/core/components/auth/store/auth";
import { useDebounce } from "@/core/utils/debounce";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

const useMemberInput = () => {
  const {
    userInfo: { user },
  } = useAuth();

  const [searchParams, setSearchParams] = useState<GetMembersParams>({
    page: 1,
    limit: 10,
    search: "",
    filters: [],
  });

  const debouncedSearch = useDebounce(searchParams.search, 300);
  const debouncedParams = useMemo(() => {
    const baseFilters =
      searchParams.filters?.filter((filter) => filter.id !== "name") || [];

    const filters = debouncedSearch
      ? [
          ...baseFilters,
          {
            id: "name",
            value: debouncedSearch,
          },
        ]
      : baseFilters;

    const params = {
      ...searchParams,
      search: debouncedSearch,
      filters,
    };

    return params;
  }, [searchParams, debouncedSearch]);

  const {
    data: response,
    error,
    isLoading,
  } = useQuery<GetMembersResponse>({
    queryKey: ["member-input", debouncedParams],
    queryFn: async ({ signal }) =>
      await getMembers(user.leagues_id[0], debouncedParams, signal),
  });

  const data = useMemo(() => response?.data || [], [response]);
  const pageCount = useMemo(() => response?.totalPages || 1, [response]);
  const total = response?.total;

  return {
    data,
    pageCount,
    total,
    error,
    isLoading,
    searchParams,
    setSearchParams,
  };
};

export { useMemberInput };
